/**
 * Date VALUE helpers — not display formatting.
 *
 * These produce a `YYYY-MM-DD` **value** that is handed to something else:
 * an `<input type="date">` default, or a query parameter sent to the backend.
 * Nothing here renders text for a human to read.
 *
 * Split out of `dateFormatter.js` on purpose. That module is being converged onto
 * `Asia/Riyadh` for display (`tasks/force-riyadh-timezone.md`); these helpers must
 * NOT follow it by accident, because changing them changes what the app *sends*
 * rather than what it *shows*.
 *
 * Two families live here, and the names say which is which:
 *   `*LocalIso` / `toInputDateValue` — **browser-local** (legacy, see below)
 *   `*Riyadh*`                       — genuinely `BUSINESS_TIMEZONE`-aware
 * Never retrofit a browser-local helper to be tz-aware; add an explicitly-named
 * one beside it, exactly as the backend did with `formatInTimezone`
 * (docs/03_Technical/TIMEZONE_IMPLEMENTATION_STATUS.md).
 *
 * The browser-local family's behaviour is **unchanged** from when these lived in
 * `dateFormatter.js`. That is a known gap, not an endorsement — the backend
 * resolves business dates in Riyadh (`be/utils/businessCalendar.js`), so a
 * non-Riyadh browser can disagree with the server about which day "today" is.
 * Deliberately out of scope for the display work and tracked separately.
 *
 * @see web/src/utils/dateFormatter.js — display formatting (do not put values there)
 * @see docs/03_Technical/TIMEZONE_IMPLEMENTATION_STATUS.md — the rules of record
 */

import moment from 'moment-timezone';
import { BUSINESS_TIMEZONE } from '../config/timezoneConfig';

/**
 * Format any Date as YYYY-MM-DD in the user's browser timezone
 * @param {Date} d - Date to format
 * @returns {string} YYYY-MM-DD
 */
export const toLocalIso = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Today's date in browser-local timezone as YYYY-MM-DD.
 *
 * Use this for `<input type="date">` defaults — never `toISOString().split('T')[0]`
 * which returns UTC and produces yesterday's date during the late-night UTC-offset
 * window (e.g. 00:00–02:59 Riyadh, 22:00–23:59 London).
 *
 * @returns {string} YYYY-MM-DD in the user's browser timezone
 */
export const todayLocalIso = () => toLocalIso(new Date());

/**
 * Prefill an `<input type="date">` from a stored value (Mongo Date or ISO string).
 *
 * Tolerant version of `toLocalIso` — returns null instead of throwing on absent
 * or unparseable input, which is what an input default needs.
 *
 * NEVER use `toISOString().split('T')[0]` for this — it shifts to UTC and bleeds
 * the previous day for users east of GMT (the entire Saudi market).
 *
 * @param {string|Date} raw - Stored date value
 * @returns {string|null} YYYY-MM-DD in the user's browser timezone, or null
 */
export const toInputDateValue = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  return toLocalIso(d);
};

/* ------------------------------------------------------------------------- *
 * Riyadh-aware family — for `<input type="datetime-local">`.
 *
 * A datetime-local field has NO timezone: it hands you a naive
 * "YYYY-MM-DDTHH:MM" and the browser reads it back in ITS OWN zone. The
 * backend composes these against Riyadh (be/utils/bookingOperationalTime.js
 * → safeComposeRiyadh), so the browser must do the same or a time entered
 * outside Saudi is stored — and acted on — at the wrong hour.
 * ------------------------------------------------------------------------- */

/**
 * Parse to a valid Date, or null. Handing moment a Date rather than an unknown
 * string avoids its "falls back to js Date()" deprecation warning on bad input.
 * @param {string|Date} raw
 * @returns {Date|null}
 */
const toValidDate = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Stored instant → the value a `<input type="datetime-local">` should show,
 * expressed as the Riyadh wall-clock reading of that instant.
 *
 * ⚠️ Built on `Intl`, NOT on moment — same reason as `riyadhDayIso` below. With
 * moment's GLOBAL locale flipped to Arabic by another page, this returned
 * `٢٠٢٦-٠٩-٢٢T١٠:٠٠`, which a datetime-local input rejects and renders EMPTY. On the
 * operational-settings card that blanked a stored arrival/departure — and saving the
 * other field then cleared it; on the checkout record it lost the "now" prefill
 * (fixed 2026-09-22, found in the Phase 2 walkthrough). `hourCycle: 'h23'` keeps
 * midnight as "00", never "24".
 *
 * @param {string|Date} instant
 * @returns {string} "YYYY-MM-DDTHH:MM", or '' when absent/invalid
 */
export const toRiyadhInputValue = (instant) => {
  const d = toValidDate(instant);
  if (!d) return '';

  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: BUSINESS_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(d).map(({ type, value }) => [type, value])
  );
  const value = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value) ? value : '';
};

/**
 * A `<input type="datetime-local">` value → the instant it denotes, reading the
 * naive wall-clock as Riyadh time. Inverse of `toRiyadhInputValue`.
 * @param {string} value - "YYYY-MM-DDTHH:MM"
 * @returns {string|null} ISO instant, or null when absent/invalid
 */
// Parsing is unaffected by the global-locale defect above: moment's Arabic locale
// accepts Western digits on input (verified 2026-09-22), and every caller feeds it a
// datetime-local value, which is always Western digits.
export const riyadhInputValueToIso = (value) => {
  if (!value) return null;
  const m = moment.tz(value, 'YYYY-MM-DDTHH:mm', BUSINESS_TIMEZONE);
  return m.isValid() ? m.toISOString() : null;
};

/**
 * Riyadh calendar day of an instant, as YYYY-MM-DD.
 *
 * Use instead of `toISOString().slice(0, 10)` whenever the day must match what
 * the screen shows — that shortcut returns the UTC day, which is a day behind
 * for anything stored at Riyadh midnight.
 *
 * ⚠️ Built on `Intl`, NOT on moment — for the reason spelled out on
 * `businessMonthKey` below, PLUS one this helper hit in production code:
 * moment's locale is a single GLOBAL setting, and pages set it to Arabic on
 * import (`BookingCalendar`, `messages/MessageDetail`; `OwnerDashboard` did too
 * until it was deleted with the owner app web switch, 4 Nov 2026). Once
 * any of them loads, `moment().format('YYYY-MM-DD')` returns **Arabic-Indic
 * digits** — `٢٠٢٦-٠٨-٢٨`. That is correct for display and silently wrong for a
 * value: it is not a valid `<input type="date">` value, it does not compare or
 * sort against an ISO string, and `Number()` on any part of it is NaN. `Intl`
 * with `en-CA` has no global locale to be flipped out from under it.
 *
 * @param {string|Date} instant
 * @returns {string} YYYY-MM-DD, or '' when absent/invalid
 */
export const riyadhDayIso = (instant) => {
  const d = toValidDate(instant);
  if (!d) return '';

  const iso = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(d);

  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : '';
};

/**
 * The business MONTH an instant falls in, as YYYY-MM.
 *
 * The month-granularity sibling of `riyadhDayIso`, and it exists for the same reason:
 * a page that derives "this month" from the browser clock is wrong for the first three
 * hours of every month in any timezone behind Riyadh — and where a month IS the query
 * (the billing worksheet), the operator silently lands on the wrong one.
 *
 * Mirrors `currentBusinessMonth` in `be/utils/businessCalendar.js`.
 *
 * ⚠️ Built on `Intl`, NOT on moment, unlike its `riyadhDayIso` sibling — deliberately.
 * `Intl` timezone support is part of the JS engine; moment-timezone's depends on its
 * data bundle actually reaching the browser, and when that fails `.format()` returns
 * the STRING "Invalid date" rather than throwing. A caller slicing that gets
 * "Invalid", parses NaN, and renders a page of dashes with nothing in the console —
 * a silent-wrong-answer failure mode this helper must not have. `en-CA` is the
 * standard trick for a YYYY-MM-DD from Intl.
 *
 * @param {string|Date} instant
 * @returns {string} YYYY-MM, or '' when absent/invalid
 */
export const businessMonthKey = (instant) => {
  const d = toValidDate(instant);
  if (!d) return '';

  const iso = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(d);

  return /^\d{4}-\d{2}/.test(iso) ? iso.slice(0, 7) : '';
};
